import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Loader2, CheckCircle, Mail } from 'lucide-react';
import axios from 'axios';
import { useGoogleAuth } from '../hooks/useGoogleAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState('es');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'sent' | 'already_verified'

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = useGoogleAuth({
    onSuccess: (user) => {
      if (user.role === 'admin') navigate('/admin', { replace: true });
      else if (user.role === 'assistant') navigate('/assistant', { replace: true });
      else navigate('/dashboard', { replace: true });
    },
    onError: (msg) => setError(msg),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!email.trim()) { setError('Please enter your email.'); return; }
    if (!acceptedTerms) { setError('You must accept the terms and conditions.'); return; }

    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/register-lead`, {
        email: email.trim(),
        name: name.trim(),
        accepted_terms: true,
        language,
      });
      if (res.data.status === 'already_verified') {
        setStep('already_verified');
      } else {
        setStep('sent');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, { email: email.trim() });
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend. Try again.');
    }
    setLoading(false);
  };

  // Success: email sent
  if (step === 'sent') {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <Link to="/" className="inline-block">
              <img src={`${API_URL}/api/uploads/og_logo.png`} alt="OG MUSIC GROUP" className="h-16 mx-auto" />
            </Link>
          </div>
          <div className="glass-card p-8 text-center" data-testid="register-email-sent">
            <div className="w-16 h-16 rounded-full bg-[#E8720C]/10 border border-[#E8720C]/20 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-[#E8720C]" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Check Your Email</h2>
            <p className="text-white/50 text-sm mb-4">
              We sent a verification link to <strong className="text-white/80">{email}</strong>.
              Click the link to continue with your registration.
            </p>
            <p className="text-white/30 text-xs mb-6">The link expires in 24 hours. Check your spam folder if you don't see it.</p>
            <button onClick={handleResend} disabled={loading} className="text-[#E8720C] text-sm hover:underline" data-testid="register-resend-btn">
              {loading ? 'Sending...' : "Didn't receive it? Resend verification email"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Already verified → redirect to set password
  if (step === 'already_verified') {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <Link to="/" className="inline-block">
              <img src={`${API_URL}/api/uploads/og_logo.png`} alt="OG MUSIC GROUP" className="h-16 mx-auto" />
            </Link>
          </div>
          <div className="glass-card p-8 text-center" data-testid="register-already-verified">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Email Already Verified</h2>
            <p className="text-white/50 text-sm mb-6">Your email is verified. Set your password to complete registration.</p>
            <Link to={`/set-password?email=${encodeURIComponent(email)}`} className="btn-gold py-3 px-8 inline-block" data-testid="register-go-set-password">
              Set Password
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Name + Email + Terms form
  return (
    <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <img src={`${API_URL}/api/uploads/og_logo.png`} alt="OG MUSIC GROUP" className="h-16 mx-auto" data-testid="register-logo" />
          </Link>
        </div>
        <div className="glass-card p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold mb-2">{t('auth.createAccount')}</h1>
            <p className="text-white/50 text-sm">{t('auth.joinCommunity')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Google Auth Button */}
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button type="button" onClick={() => googleLogin()}
              className="w-full flex items-center justify-center gap-3 py-3.5 bg-white/[0.07] hover:bg-white/[0.12] border border-white/10 rounded-xl text-sm font-medium text-white transition-colors"
              data-testid="google-register-btn">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Registrarse con Google
            </button>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-white/30">o con email</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">{t('auth.fullName')}</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full input-glass"
                placeholder="John Doe"
                required
                data-testid="register-name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">{t('auth.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full input-glass"
                placeholder="you@example.com"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Preferred Language</label>
              <div className="flex gap-2">
                {[
                  { id: 'es', label: 'Español' },
                  { id: 'en', label: 'English' },
                  { id: 'pt', label: 'Português' },
                ].map(lang => (
                  <button
                    key={lang.id}
                    type="button"
                    onClick={() => setLanguage(lang.id)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${language === lang.id ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20'}`}
                    data-testid={`register-lang-${lang.id}`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 rounded border-white/20 bg-white/5"
                data-testid="register-terms-checkbox"
              />
              <span className="text-white/60">
                {t('auth.agreeTerms')}{' '}
                <Link to="/terms" className="text-[#E8720C] hover:underline">{t('footer.terms')}</Link>
                {' '}{t('auth.and')}{' '}
                <Link to="/privacy" className="text-[#E8720C] hover:underline">{t('footer.privacy')}</Link>
              </span>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-gold py-4 flex items-center justify-center gap-2"
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                'Verify Email & Continue'
              )}
            </button>
          </form>

          <p className="text-center text-sm text-white/50 mt-6">
            {t('auth.haveAccount')}{' '}
            <Link to="/login" className="text-[#E8720C] hover:underline">{t('auth.signInLink')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
